import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Clock, ChevronLeft, ChevronRight, Send, Languages, Video } from 'lucide-react';
import ChunkedUploader from '@/utils/chunkedUploader';
import UploadProgress from './UploadProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TakeExam = () => {
  const { t } = useTranslation();
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(true);
  const [studentInfo, setStudentInfo] = useState({ name: '', email: '' });
  const [translated, setTranslated] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [showRecordingConsent, setShowRecordingConsent] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  
  // Use refs for recording data to avoid state update timing issues
  const cameraStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const cameraRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const cameraChunksRef = useRef([]);
  const screenChunksRef = useRef([]);
  const examRef = useRef(null);  // Add ref to track exam data
  const cameraUploaderRef = useRef(null);  // Chunked uploader for camera
  const screenUploaderRef = useRef(null);  // Chunked uploader for screen
  const recordingIdRef = useRef(null);  // Store recording ID

  useEffect(() => {
    fetchExamData();
  }, [examId]);
  
  // Prevent user from closing/refreshing during exam
  useEffect(() => {
    if (examStarted && !submitting) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'الامتحان لم يكتمل بعد. هل أنت متأكد من الخروج؟';
        return 'الامتحان لم يكتمل بعد. هل أنت متأكد من الخروج؟';
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [examStarted, submitting]);

  useEffect(() => {
    // Only run timer if exam has actually started (after recording)
    if (examStarted && timeLeft > 0) {
      // Warning alerts before time runs out
      if (timeLeft === 300) {
        // 5 minutes warning
        toast.warning('⚠️ تبقى 5 دقائق فقط على انتهاء الامتحان!', { duration: 5000 });
        console.log('⚠️ 5 minutes remaining');
      } else if (timeLeft === 60) {
        // 1 minute warning
        toast.error('⚠️ تبقت دقيقة واحدة فقط! سيتم التسليم التلقائي عند انتهاء الوقت', { duration: 5000 });
        console.log('⚠️ 1 minute remaining - auto-submit warning');
      } else if (timeLeft === 10) {
        // 10 seconds final warning
        toast.error('⏰ 10 ثوان فقط!', { duration: 3000 });
        console.log('⏰ 10 seconds remaining');
      }
      
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (examStarted && timeLeft === 0 && exam && !submitting) {
      // ⏰ Time's up! Auto-submit the exam
      console.log('⏰ Time expired - Auto-submitting exam...');
      toast.error('⏰ انتهى وقت الامتحان! جاري التسليم التلقائي...', { duration: 5000, icon: '⏰' });
      
      // Small delay to show the warning message
      setTimeout(() => {
        handleSubmit();
      }, 500);
    }
  }, [timeLeft, examStarted, submitting]);

  const fetchExamData = async () => {
    console.log('📥 fetchExamData called for examId:', examId);
    try {
      console.log('📡 Fetching exam data from API...');
      const response = await axios.get(`${API}/student/exams/${examId}/questions`);
      console.log('📊 API response received:', response.data);
      console.log('📝 Exam data:', response.data.exam);
      console.log('📝 Questions count:', response.data.questions?.length);
      
      const examData = response.data.exam;
      setExam(examData);
      examRef.current = examData;  // Update ref immediately
      setQuestions(response.data.questions);
      console.log('✅ Exam and questions set in state and ref');
      // DON'T set timeLeft here - only set it when exam actually starts after recording
      
      // Try to load draft answers if they exist (in case of page refresh)
      try {
        const draftResponse = await axios.get(`${API}/student/exams/${examId}/draft`);
        if (draftResponse.data.success && draftResponse.data.answers.length > 0) {
          // Convert array of answers to object keyed by question_id
          const savedAnswers = {};
          draftResponse.data.answers.forEach(ans => {
            savedAnswers[ans.question_id] = ans.selected_answer;
          });
          setAnswers(savedAnswers);
          console.log(`✅ Restored ${draftResponse.data.total_answers} saved answers from draft`);
          toast.info(`تم استعادة ${draftResponse.data.total_answers} إجابة محفوظة`, { duration: 3000 });
        }
      } catch (draftError) {
        // No draft found or error - that's okay, start fresh
        console.log('No draft answers found, starting fresh');
      }
    } catch (error) {
      console.error('❌ Error fetching exam data:', error);
      toast.error(t('error'));
      navigate('/student/exams');
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  };

  const handleAnswerChange = async (questionId, answer) => {
    // Update local state immediately
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Auto-save answer to backend (fire and forget - don't wait)
    try {
      await axios.post(`${API}/student/exams/save-answer`, {
        exam_id: examId,
        student_name: studentInfo.name,
        student_email: studentInfo.email,
        question_id: questionId,
        selected_answer: answer
      });
      console.log(`✅ Auto-saved answer for question ${questionId}: ${answer}`);
    } catch (error) {
      console.error('⚠️ Failed to auto-save answer (will retry on submit):', error);
      // Don't show error to user - it will be saved on final submit
    }
  };

  const startRecording = async () => {
    try {
      console.log('🎬 بدء عملية التسجيل...');
      console.log('📊 Browser:', navigator.userAgent);
      
      // Generate recording ID
      const recordingId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      recordingIdRef.current = recordingId;
      console.log('📝 Recording ID:', recordingId);
      
      // Initialize chunked uploaders (V1 with no server-side compression)
      cameraUploaderRef.current = new ChunkedUploader(recordingId, 'camera');
      screenUploaderRef.current = new ChunkedUploader(recordingId, 'screen');
      
      // Setup progress callbacks
      cameraUploaderRef.current.onProgress((progress) => {
        console.log(`📹 Camera upload: ${progress.uploaded}/${progress.total} chunks`);
      });
      
      screenUploaderRef.current.onProgress((progress) => {
        console.log(`🖥️ Screen upload: ${progress.uploaded}/${progress.total} chunks`);
      });
      
      // Clear previous chunks (if any)
      cameraChunksRef.current = [];
      screenChunksRef.current = [];
      
      // ============= STEP 1: CAMERA RECORDING (MANDATORY) =============
      console.log('📹 Step 1: طلب الوصول للكاميرا (إلزامي)...');
      toast.info('📹 طلب الوصول إلى الكاميرا (إلزامي)...', { duration: 3000 });
      
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const error = new Error('المتصفح لا يدعم تسجيل الكاميرا');
        console.error('❌ Browser does not support getUserMedia');
        throw error;
      }
      
      // Request camera access
      let cameraStream;
      try {
        cameraStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }, 
          audio: true 
        });
        console.log('✅ تم الحصول على stream الكاميرا');
        console.log('Camera tracks:', cameraStream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));
      } catch (cameraError) {
        console.error('❌ فشل طلب الكاميرا:', {
          name: cameraError.name,
          message: cameraError.message,
          stack: cameraError.stack
        });
        
        let errorMessage = 'يجب السماح بالوصول إلى الكاميرا لبدء الاختبار';
        
        if (cameraError.name === 'NotAllowedError' || cameraError.name === 'PermissionDeniedError') {
          errorMessage = '❌ تم رفض الوصول للكاميرا\n\nيجب الموافقة على تسجيل الكاميرا لبدء الاختبار';
        } else if (cameraError.name === 'NotFoundError') {
          errorMessage = '❌ لم يتم العثور على كاميرا\n\nيجب وجود كاميرا متصلة بالجهاز';
        } else if (cameraError.name === 'NotReadableError') {
          errorMessage = '❌ الكاميرا مستخدمة من تطبيق آخر\n\nأغلق أي تطبيق يستخدم الكاميرا';
        }
        
        toast.error(errorMessage, { duration: 5000 });
        throw new Error(errorMessage);
      }
      
      cameraStreamRef.current = cameraStream;

      // Setup camera recorder
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.log('⚠️ vp9 not supported, falling back to webm');
        mimeType = 'video/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.log('⚠️ webm not supported, falling back to mp4');
        mimeType = 'video/mp4';
      }
      console.log('📹 Using MIME type:', mimeType);
      
      const cameraRec = new MediaRecorder(cameraStream, { 
        mimeType,
        videoBitsPerSecond: 2500000
      });
      
      cameraRec.ondataavailable = (e) => {
        if (e.data.size > 0) {
          console.log(`📹 Camera chunk: ${(e.data.size / 1024 / 1024).toFixed(2)} MB`);
          // Upload chunk immediately
          if (cameraUploaderRef.current) {
            cameraUploaderRef.current.addChunk(e.data);
          }
        }
      };

      cameraRec.onstart = () => {
        console.log('✅ Camera recorder onstart event fired');
      };
      
      cameraRec.onerror = (error) => {
        console.error('❌ Camera recorder error event:', error);
      };

      cameraRecorderRef.current = cameraRec;
      
      // Start recording with 60 second chunks for efficient upload
      cameraRec.start(60000); // 60 seconds per chunk
      console.log('📹 Camera recorder.start() called (60s chunks), state:', cameraRec.state);
      
      // Wait for recording to start (increased timeout)
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          console.log('📹 Checking camera state:', cameraRec.state);
          if (cameraRec.state === 'recording') {
            clearInterval(checkInterval);
            console.log('✅ Camera is now recording');
            resolve();
          }
        }, 200);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (cameraRec.state !== 'recording') {
            reject(new Error('Camera recording timeout - state: ' + cameraRec.state));
          }
        }, 5000);
      });
      
      console.log('✅ تسجيل الكاميرا يعمل بنجاح');
      toast.success('✅ تم بدء تسجيل الكاميرا', { duration: 2000 });

      // ============= STEP 2: SCREEN RECORDING (MANDATORY) =============
      console.log('🖥️ Step 2: طلب الوصول لتسجيل الشاشة (إلزامي)...');
      toast.info('🖥️ طلب تسجيل الشاشة (إلزامي)...', { duration: 3000 });
      
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        const error = new Error('المتصفح لا يدعم تسجيل الشاشة');
        console.error('❌ Browser does not support getDisplayMedia');
        
        // Stop camera before throwing
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (cameraRecorderRef.current) {
          cameraRecorderRef.current.stop();
        }
        
        throw error;
      }
      
      // Request screen recording
      let screenStream;
      try {
        screenStream = await navigator.mediaDevices.getDisplayMedia({ 
          video: { 
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
          }, 
          audio: false
        });
        console.log('✅ تم الحصول على stream الشاشة');
        console.log('Screen tracks:', screenStream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));
      } catch (screenError) {
        console.error('❌ فشل طلب تسجيل الشاشة:', {
          name: screenError.name,
          message: screenError.message,
          stack: screenError.stack
        });
        
        let errorMessage = 'يجب السماح بتسجيل الشاشة لبدء الاختبار';
        
        if (screenError.name === 'NotAllowedError' || screenError.name === 'PermissionDeniedError') {
          errorMessage = '❌ تم رفض تسجيل الشاشة\n\nيجب الموافقة على تسجيل الشاشة لبدء الاختبار';
        } else if (screenError.name === 'NotFoundError') {
          errorMessage = '❌ لم يتم اختيار شاشة للتسجيل';
        }
        
        // Stop camera recording if screen failed
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (cameraRecorderRef.current) {
          cameraRecorderRef.current.stop();
        }
        
        toast.error(errorMessage, { duration: 5000 });
        throw new Error(errorMessage);
      }
      
      screenStreamRef.current = screenStream;
      
      // Handle user stopping screen share manually
      screenStream.getVideoTracks()[0].onended = () => {
        console.log('⚠️ المستخدم أوقف مشاركة الشاشة');
        toast.warning('⚠️ تم إيقاف مشاركة الشاشة. يرجى إعادة تشغيلها.', { duration: 5000 });
      };

      // Setup screen recorder
      let screenMimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(screenMimeType)) {
        console.log('⚠️ vp9 not supported for screen, falling back to webm');
        screenMimeType = 'video/webm';
      }
      if (!MediaRecorder.isTypeSupported(screenMimeType)) {
        console.log('⚠️ webm not supported for screen, falling back to mp4');
        screenMimeType = 'video/mp4';
      }
      console.log('🖥️ Using MIME type for screen:', screenMimeType);
      
      const screenRec = new MediaRecorder(screenStream, { 
        mimeType: screenMimeType,
        videoBitsPerSecond: 3000000
      });
      
      screenRec.ondataavailable = (e) => {
        if (e.data.size > 0) {
          console.log(`🖥️ Screen chunk: ${(e.data.size / 1024 / 1024).toFixed(2)} MB`);
          // Upload chunk immediately
          if (screenUploaderRef.current) {
            screenUploaderRef.current.addChunk(e.data);
          }
        }
      };

      screenRec.onstart = () => {
        console.log('✅ Screen recorder onstart event fired');
      };
      
      screenRec.onerror = (error) => {
        console.error('❌ Screen recorder error event:', error);
      };

      screenRecorderRef.current = screenRec;
      
      // Start recording with 60 second chunks for efficient upload
      screenRec.start(60000); // 60 seconds per chunk
      console.log('🖥️ Screen recorder.start() called (60s chunks), state:', screenRec.state);
      
      // Wait for recording to start (increased timeout)
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          console.log('🖥️ Checking screen state:', screenRec.state);
          if (screenRec.state === 'recording') {
            clearInterval(checkInterval);
            console.log('✅ Screen is now recording');
            resolve();
          }
        }, 200);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (screenRec.state !== 'recording') {
            // Stop camera before rejecting
            if (cameraStreamRef.current) {
              cameraStreamRef.current.getTracks().forEach(track => track.stop());
            }
            if (cameraRecorderRef.current) {
              cameraRecorderRef.current.stop();
            }
            reject(new Error('Screen recording timeout - state: ' + screenRec.state));
          }
        }, 5000);
      });
      
      console.log('✅ تسجيل الشاشة يعمل بنجاح');
      toast.success('✅ تم بدء تسجيل الشاشة', { duration: 2000 });

      // ============= STEP 3: VERIFY BOTH RECORDINGS =============
      console.log('🔍 التحقق النهائي من حالة التسجيل...');
      console.log('Camera state:', cameraRecorderRef.current?.state);
      console.log('Screen state:', screenRecorderRef.current?.state);
      
      const cameraRecording = cameraRecorderRef.current?.state === 'recording';
      const screenRecording = screenRecorderRef.current?.state === 'recording';
      
      if (!cameraRecording || !screenRecording) {
        console.error('❌ فشل التحقق النهائي من التسجيل');
        console.error('Camera recording:', cameraRecording);
        console.error('Screen recording:', screenRecording);
        
        // Stop any active recordings
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (cameraRecorderRef.current) {
          cameraRecorderRef.current.stop();
        }
        if (screenRecorderRef.current) {
          screenRecorderRef.current.stop();
        }
        
        let errorMsg = 'فشل بدء التسجيل';
        if (!cameraRecording) errorMsg = 'فشل تسجيل الكاميرا';
        if (!screenRecording) errorMsg = 'فشل تسجيل الشاشة';
        if (!cameraRecording && !screenRecording) errorMsg = 'فشل تسجيل الكاميرا والشاشة';
        
        throw new Error(errorMsg);
      }
      
      // ✅ Both recordings active - exam can start
      setIsRecording(true);
      
      // Success message
      toast.success('✅ تم بدء تسجيل الكاميرا والشاشة بنجاح', { 
        duration: 5000,
        icon: '🎬'
      });
      
      console.log('✅ كلا التسجيلين يعملان - يمكن بدء الاختبار');
      
      return true;
    } catch (error) {
      console.error('❌ Recording error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      // Ensure all resources are cleaned up
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
        cameraStreamRef.current = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      if (cameraRecorderRef.current && cameraRecorderRef.current.state !== 'inactive') {
        cameraRecorderRef.current.stop();
      }
      if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
        screenRecorderRef.current.stop();
      }
      
      setIsRecording(false);
      throw error;
    }
  };

  const stopRecording = async () => {
    return new Promise((resolve) => {
      let cameraStopped = false;
      let screenStopped = false;

      const checkBothStopped = async () => {
        if (cameraStopped && screenStopped) {
          console.log('✅ Both recordings stopped');
          setIsRecording(false);
          
          // Finalize uploads with retry logic
          const finalizeWithRetry = async (uploader, type, maxRetries = 3) => {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log(`🔄 Finalizing ${type} (attempt ${attempt}/${maxRetries})...`);
                const result = await uploader.finalize();
                console.log(`✅ ${type} upload finalized`, result);
                return result;
              } catch (error) {
                console.error(`❌ ${type} finalization attempt ${attempt} failed:`, error);
                if (attempt === maxRetries) {
                  throw error;
                }
                // Wait before retry (exponential backoff)
                const delay = 2000 * Math.pow(2, attempt - 1);
                console.log(`⏳ Retrying in ${delay}ms...`);
                await new Promise(r => setTimeout(r, delay));
              }
            }
          };
          
          try {
            toast.loading('جاري إنهاء رفع التسجيلات... يرجى الانتظار', { id: 'finalize' });
            
            const results = await Promise.allSettled([
              cameraUploaderRef.current ? finalizeWithRetry(cameraUploaderRef.current, 'camera') : Promise.resolve(),
              screenUploaderRef.current ? finalizeWithRetry(screenUploaderRef.current, 'screen') : Promise.resolve()
            ]);
            
            const failures = results.filter(r => r.status === 'rejected');
            
            if (failures.length === 0) {
              toast.success('✅ تم رفع جميع التسجيلات بنجاح', { id: 'finalize' });
            } else if (failures.length === results.length) {
              toast.error('❌ فشل رفع جميع التسجيلات', { id: 'finalize' });
              console.error('All finalizations failed:', failures);
            } else {
              toast.warning('⚠️ تم رفع بعض التسجيلات - قد تكون بعض الملفات غير مكتملة', { id: 'finalize' });
              console.warn('Partial finalization success:', results);
            }
            
          } catch (error) {
            console.error('❌ Error finalizing uploads:', error);
            toast.error('فشل إنهاء رفع التسجيلات - سيتم المحاولة لاحقاً', { id: 'finalize' });
          }
          
          resolve();
        }
      };

      // Stop camera recorder
      if (cameraRecorderRef.current && cameraRecorderRef.current.state !== 'inactive') {
        cameraRecorderRef.current.onstop = () => {
          console.log('📹 Camera recorder stopped');
          cameraStopped = true;
          checkBothStopped();
        };
        cameraRecorderRef.current.stop();
      } else {
        cameraStopped = true;
      }

      // Stop screen recorder
      if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
        screenRecorderRef.current.onstop = () => {
          console.log('🖥️ Screen recorder stopped');
          screenStopped = true;
          checkBothStopped();
        };
        screenRecorderRef.current.stop();
      } else {
        screenStopped = true;
      }

      // Stop all tracks
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }

      // If both were already inactive, resolve immediately
      checkBothStopped();
    });
  };

  // uploadVideo removed - now using chunked upload during recording

  const handleStartExam = async () => {
    if (!studentInfo.name || !studentInfo.email) {
      toast.error('Please enter your name and email');
      return;
    }
    
    // Show recording consent dialog
    setShowStartDialog(false);
    setShowRecordingConsent(true);
  };

  const handleRecordingConsent = async () => {
    console.log('🎯 handleRecordingConsent called');
    
    try {
      setShowRecordingConsent(false);
      console.log('✅ Dialog closed');
      
      // Show loading message
      toast.info('جاري بدء التسجيل...');
      
      // Wait for exam data if not loaded yet (race condition protection)
      let retries = 0;
      while (!examRef.current && retries < 10) {
        console.log(`⏳ Waiting for exam data... (attempt ${retries + 1}/10)`);
        await new Promise(resolve => setTimeout(resolve, 200));
        retries++;
      }
      
      // Verify exam data is available
      const currentExam = examRef.current;
      if (!currentExam || !currentExam.duration_minutes) {
        console.error('❌ Exam data not available after waiting!');
        console.error('ExamRef:', examRef.current);
        console.error('Exam state:', exam);
        throw new Error('بيانات الامتحان غير متوفرة. يرجى المحاولة مرة أخرى.');
      }
      
      console.log('✅ Exam data confirmed:', { 
        id: currentExam.id, 
        title: currentExam.title, 
        duration: currentExam.duration_minutes 
      });
      
      // Start recording and WAIT for it to complete
      console.log('🎬 Starting recording...');
      const recordingResult = await startRecording();
      console.log('✅ Recording started successfully:', recordingResult);
      
      // Recording started successfully - NOW start the exam
      console.log('✅ Recording confirmed - Starting exam now');
      const timerSeconds = currentExam.duration_minutes * 60;
      console.log('⏰ Setting timer to:', timerSeconds, 'seconds');
      
      // Set exam started flag FIRST
      setExamStarted(true);
      console.log('✅ Exam started flag set');
      
      // THEN start the timer
      setTimeLeft(timerSeconds);
      console.log('✅ Timer set');
      
      // Confirm exam started
      toast.success('تم بدء الامتحان بنجاح');
      console.log('✅✅✅ EXAM STARTED SUCCESSFULLY ✅✅✅');
    } catch (error) {
      console.error('❌❌❌ ERROR IN handleRecordingConsent ❌❌❌');
      console.error('Error:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack
      });
      
      // Make sure exam doesn't start
      setExamStarted(false);
      setTimeLeft(0);
      
      // Show detailed error message in Arabic
      let errorMsg = 'فشل بدء الامتحان';
      
      // Add more specific guidance based on error
      if (error.message) {
        if (error.message.includes('Camera') || error.message.includes('كاميرا')) {
          errorMsg = '❌ يجب السماح بالكاميرا لبدء الامتحان';
        } else if (error.message.includes('Screen') || error.message.includes('شاشة')) {
          errorMsg = '❌ يجب السماح بتسجيل الشاشة لبدء الامتحان';
        } else if (error.message.includes('denied') || error.message.includes('رفض')) {
          errorMsg = '❌ تم رفض الأذونات - يجب الموافقة على الكاميرا والشاشة';
        } else if (error.message.includes('timeout')) {
          errorMsg = '⏱️ انتهى الوقت - يرجى المحاولة مرة أخرى';
        } else if (error.message.includes('بيانات الامتحان')) {
          errorMsg = error.message;
        } else {
          errorMsg = `فشل: ${error.message}`;
        }
      }
      
      toast.error(errorMsg, { duration: 5000 });
      toast.info('💡 يرجى تحديث الصفحة والمحاولة مرة أخرى', { duration: 4000 });
      
      // Wait a bit so user sees the message
      setTimeout(() => {
        navigate('/student/exams');
      }, 3000);
    }
  };

  const detectLanguage = (text) => {
    // Simple language detection: check for Arabic characters
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text) ? 'ar' : 'en';
  };

  const translateText = async (text, sourceLang, targetLang) => {
    try {
      // Use Google Translate API with dynamic language detection
      const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`);
      if (!response.ok) throw new Error('Translation failed');
      const data = await response.json();
      
      // Combine all translated segments
      let translatedText = '';
      if (data && data[0]) {
        for (let i = 0; i < data[0].length; i++) {
          if (data[0][i][0]) {
            translatedText += data[0][i][0];
          }
        }
      }
      return translatedText || text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  };

  const handleTranslate = async () => {
    if (translated) {
      // Switch back to original language
      setQuestions(originalQuestions);
      setTranslated(false);
      toast.success(t('returnedToOriginal') || 'Returned to original language');
    } else {
      // Translate all questions
      setTranslating(true);
      
      // Detect source language from first question
      const sourceLang = detectLanguage(questions[0]?.question_text || '');
      const targetLang = sourceLang === 'ar' ? 'en' : 'ar';
      
      const translationMessage = sourceLang === 'ar' 
        ? (t('translatingToEnglish') || 'Translating to English...')
        : (t('translatingToArabic') || 'Translating to Arabic...');
      
      toast.info(translationMessage);
      
      try {
        // Save original questions
        setOriginalQuestions([...questions]);
        
        // Translate all questions
        const translatedQuestions = await Promise.all(
          questions.map(async (question) => {
            try {
              // Add small delay between translations to avoid rate limiting
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const [translatedQ, translatedA, translatedB, translatedC, translatedD] = await Promise.all([
                translateText(question.question_text, sourceLang, targetLang),
                translateText(question.option_a, sourceLang, targetLang),
                translateText(question.option_b, sourceLang, targetLang),
                translateText(question.option_c, sourceLang, targetLang),
                translateText(question.option_d, sourceLang, targetLang)
              ]);
              
              return {
                ...question,
                question_text: translatedQ,
                option_a: translatedA,
                option_b: translatedB,
                option_c: translatedC,
                option_d: translatedD
              };
            } catch (error) {
              console.error(`Error translating question ${question.id}:`, error);
              return question; // Keep original if translation fails
            }
          })
        );
        
        setQuestions(translatedQuestions);
        setTranslated(true);
        toast.success(t('translationComplete') || 'Translation complete');
      } catch (error) {
        console.error('Translation error:', error);
        toast.error(t('translationFailed') || 'Translation failed');
      } finally {
        setTranslating(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (submitting) {
      console.log('⚠️ Already submitting, ignoring duplicate call');
      return;
    }
    
    // Check if camera recording is active
    const hasCameraRecording = cameraStreamRef.current && cameraRecorderRef.current;
    const cameraHasChunks = cameraChunksRef.current.length > 0;
    
    console.log(`📹 Recording status:
      Camera stream: ${!!cameraStreamRef.current}
      Camera recorder: ${!!cameraRecorderRef.current}
      Camera chunks: ${cameraChunksRef.current.length}
      Screen chunks: ${screenChunksRef.current.length}
    `);
    
    // Warning if no camera recording (camera is mandatory)
    if (!hasCameraRecording && !cameraHasChunks) {
      const confirmSubmit = window.confirm(
        '⚠️ تحذير: لم يتم اكتشاف تسجيل الكاميرا!\n\n' +
        'قد لا يتم قبول الامتحان بدون تسجيل.\n\n' +
        'هل تريد المتابعة؟'
      );
      
      if (!confirmSubmit) {
        return;
      }
    }
    
    console.log('📤 Starting exam submission...');
    setSubmitting(true);
    
    try {
      // Count answered vs unanswered questions
      const answeredCount = questions.filter(q => answers[q.id]).length;
      const unansweredCount = questions.length - answeredCount;
      
      console.log(`📊 Answers: ${answeredCount}/${questions.length} answered, ${unansweredCount} unanswered`);
      
      if (unansweredCount > 0) {
        console.log(`⚠️ ${unansweredCount} unanswered questions will be marked as WRONG (null answer)`);
      }
      
      const submission = {
        exam_id: examId,
        student_name: studentInfo.name,
        student_email: studentInfo.email,
        answers: questions.map(q => ({
          question_id: q.id,
          selected_answer: answers[q.id] || null  // null = unanswered = WRONG
        }))
      };

      console.log('📤 Submitting answers to backend...');
      const response = await axios.post(`${API}/student/exams/submit`, submission);
      const subId = response.data.id;
      
      console.log(`✅ Submission successful - ID: ${subId}`);
      toast.success(t('examSubmitted') || 'تم تسليم الامتحان بنجاح');
      
      // Stop recording and finalize uploads
      await stopRecording();
      console.log('✅ Recording stopped and uploads finalized');
      
      // Navigate immediately (don't wait for video upload)
      toast.success('يمكنك المغادرة الآن - التسجيلات ستكتمل تلقائياً', { duration: 4000 });
      setTimeout(() => {
        navigate('/student/results');
      }, 1000);  // Much shorter wait!
    } catch (error) {
      toast.error(t('error'));
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  // Don't show questions until exam actually starts (after recording)
  if (!examStarted) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="take-exam">
        {/* Dialogs will be shown here */}
        {/* Start Exam Dialog */}
        <Dialog open={showStartDialog} onOpenChange={(open) => {
          if (!open) {
            navigate('/student/exams');
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('startExam')}</DialogTitle>
              <DialogDescription>
                {t('studentName')} & {t('studentEmail')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('studentName')}</Label>
                <Input
                  data-testid="student-name-input"
                  value={studentInfo.name}
                  onChange={(e) => setStudentInfo({...studentInfo, name: e.target.value})}
                  placeholder={t('studentName')}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t('studentEmail')}</Label>
                <Input
                  data-testid="student-email-input"
                  type="email"
                  value={studentInfo.email}
                  onChange={(e) => setStudentInfo({...studentInfo, email: e.target.value})}
                  placeholder={t('studentEmail')}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/student/exams')}
                  className="flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button
                  data-testid="start-exam-submit"
                  onClick={handleStartExam}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-indigo-600"
                >
                  {t('continue')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Recording Consent Dialog */}
        <Dialog open={showRecordingConsent} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 text-base">
                <Video className="h-5 w-5" />
                {t('recordingNotice')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Arabic Notice */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-bold text-sm text-red-700 mb-2">⚠️ إشعار هام</h3>
                <div className="space-y-1.5 text-gray-800 text-sm">
                  <p className="font-medium">لضمان المصداقية والجودة:</p>
                  <ul className="list-none space-y-1 text-xs">
                    <li>📹 تسجيل الكاميرا طوال الاختبار</li>
                    <li>🖥️ تسجيل الشاشة أثناء الاختبار</li>
                    <li>🔒 للمراجعة فقط</li>
                  </ul>
                </div>
              </div>

              {/* English Notice */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-bold text-sm text-blue-700 mb-2">⚠️ Important Notice</h3>
                <div className="space-y-1.5 text-gray-800 text-sm">
                  <p className="font-medium">To ensure authenticity & quality:</p>
                  <ul className="list-none space-y-1 text-xs">
                    <li>📹 Camera recording throughout exam</li>
                    <li>🖥️ Screen recording during exam</li>
                    <li>🔒 For review purposes only</li>
                  </ul>
                </div>
              </div>

              {/* Consent Buttons */}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={handleRecordingConsent}
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  <Video className="h-4 w-4 mr-2" />
                  {t('agreeAndStart')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/student/exams')}
                  className="w-full py-2"
                  size="sm"
                >
                  {t('cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="take-exam">{/* Questions will only show after examStarted = true */}
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-r from-cyan-50 to-indigo-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{exam.title}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('question')} {currentQuestion + 1} {t('of')} {questions.length}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Button
                data-testid="translate-button"
                onClick={handleTranslate}
                variant="outline"
                size="sm"
                className="flex items-center"
                disabled={translating}
              >
                {translating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-1 md:mr-2"></div>
                    <span className="hidden sm:inline">{t('translating')}</span>
                    <Languages className="h-4 w-4 sm:hidden" />
                  </>
                ) : (
                  <>
                    <Languages className="h-4 w-4 md:mr-2" />
                    <span className="hidden sm:inline">
                      {translated 
                        ? (t('returnToOriginal') || 'Return to Original') 
                        : (detectLanguage(questions[0]?.question_text || '') === 'ar' 
                            ? (t('translateToEnglish') || 'Translate to English')
                            : (t('translateToArabic') || 'Translate to Arabic')
                          )
                      }
                    </span>
                  </>
                )}
              </Button>
              <div className="text-center">
                <div className={`text-2xl font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-cyan-600'}`}>
                  <Clock className="inline h-5 w-5 mr-1" />
                  {formatTime(timeLeft)}
                </div>
                <p className="text-xs text-gray-600">{t('timeRemaining')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {answeredCount}/{questions.length}
                </div>
                <p className="text-xs text-gray-600">{t('correctAnswers')}</p>
              </div>
            </div>
          </div>
          <Progress value={progress} className="mt-4 h-2" />
        </CardContent>
      </Card>

      {/* Question Card */}
      <Card className={`shadow-xl border-0 ${translated ? 'border-2 border-blue-300' : ''}`}>
        <CardHeader className={`${translated ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gradient-to-r from-gray-50 to-gray-100'}`}>
          <CardTitle className="text-xl leading-relaxed">
            <div className="flex items-start gap-2">
              <span className="text-cyan-600 font-bold">Q{currentQuestion + 1}.</span>
              <div className="flex-1">
                <div className={translated ? 'text-blue-900 font-medium' : ''}>
                  {question.question_text}
                </div>
                {translated && (
                  <span className="inline-block mt-2 text-xs bg-blue-500 text-white px-3 py-1 rounded-full">
                    🌐 {t('translated')} - English
                  </span>
                )}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <RadioGroup
            value={answers[question.id] || ''}
            onValueChange={(value) => handleAnswerChange(question.id, value)}
            className="space-y-4"
          >
            {['A', 'B', 'C', 'D'].map(option => (
              <div
                key={option}
                className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer
                  ${answers[question.id] === option
                    ? (translated ? 'border-blue-500 bg-blue-50' : 'border-cyan-500 bg-cyan-50')
                    : (translated ? 'border-blue-200 hover:border-blue-300 hover:bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50')
                  }`}
                onClick={() => handleAnswerChange(question.id, option)}
              >
                <RadioGroupItem value={option} id={`option-${option}`} className="mt-1" />
                <Label htmlFor={`option-${option}`} className={`flex-1 cursor-pointer leading-relaxed ${translated ? 'text-blue-900' : ''}`}>
                  <span className="font-semibold mr-2">{option}.</span>
                  {question[`option_${option.toLowerCase()}`]}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-4">
        <Button
          data-testid="previous-question"
          variant="outline"
          onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
          disabled={currentQuestion === 0}
          className="flex-1 md:flex-none"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          {t('previous')}
        </Button>

        <div className="flex gap-2 overflow-x-auto">
          {questions.slice(0, 10).map((_, index) => (
            <button
              key={index}
              data-testid={`question-nav-${index}`}
              onClick={() => setCurrentQuestion(index)}
              className={`w-10 h-10 rounded-lg font-semibold transition-all
                ${currentQuestion === index
                  ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white'
                  : answers[questions[index].id]
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
            >
              {index + 1}
            </button>
          ))}
          {questions.length > 10 && (
            <span className="flex items-center px-2 text-gray-500">...</span>
          )}
        </div>

        {currentQuestion < questions.length - 1 ? (
          <Button
            data-testid="next-question"
            onClick={() => setCurrentQuestion(currentQuestion + 1)}
            className="bg-gradient-to-r from-cyan-600 to-indigo-600 flex-1 md:flex-none"
          >
            {t('next')}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            data-testid="submit-exam-button"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-gradient-to-r from-green-600 to-emerald-600 flex-1 md:flex-none"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? t('loading') : t('submitExam')}
          </Button>
        )}
      </div>

      {/* Question Overview */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle className="text-sm">{t('questionsCount')} {t('statistics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, index) => (
              <button
                key={q.id}
                data-testid={`question-grid-${index}`}
                onClick={() => setCurrentQuestion(index)}
                className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all
                  ${currentQuestion === index
                    ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white ring-2 ring-cyan-300'
                    : answers[q.id]
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Upload Progress Dashboard */}
      {examStarted && (
        <UploadProgress 
          cameraUploader={cameraUploaderRef.current}
          screenUploader={screenUploaderRef.current}
        />
      )}
    </div>
  );
};

export default TakeExam;
